import { useEffect, useState } from "react";
import { differenceInSeconds, differenceInMinutes, differenceInHours, differenceInDays, parseISO } from "date-fns";
import { useHeaderContext } from "../../context/headerContext";
import { useUserContext } from "../../context/userContext";
import PersonCard from "../../components/PersonCard";
import fetchWithRefresh from "../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../utils/LogErrors";

export default function Incidents() {
  const { title, setBackButton, setTitle, setEmergency } = useHeaderContext();
  const { user, selectedVenue, logout, updateTokens } = useUserContext();
  const [incident, setIncident] = useState([]);  
  const [lastUpdatedText, setLastUpdatedText] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      await Sentry.startSpan({ name: "Fetch Active Incidents", op: "http.request" }, async (span) => {
        try {
          const response = await fetchWithRefresh(
            `${import.meta.env.VITE_API_URL}Incident/FetchActiveIncidentsForVenue?venueId=${selectedVenue?.OrgID}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user?.token}`,
              },
            },
            logout,
            updateTokens,
            (message) => {
              toast.info(message);
            }
          );
    
          span.setAttribute("http.status_code", response.status);
    
          if (response.ok) {
            const data = await response.json();
            setIncident(Array.isArray(data) ? data : [data]);
          } else {
            throw new Error("Failed to fetch incidents");
          }
        } catch (error) {
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, "Error fetching active incidents: " + error);
          span.setStatus("error");
        } finally {
          span.end();
        }
      });
    }; fetchData();    
  }, [title, user]);

  useEffect(() => {
    setTitle(`${incident.length} Live Incident${incident.length !== 1 ? "s" : ""}`);
    setBackButton(true);
    setEmergency(true);
  }, [incident])

  const groupedIncidents = incident.reduce((acc, curr) => {
    const date = new Date(curr.WhenOpened).toLocaleDateString('en-GB');
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(curr);
    return acc;
  }, {});

  useEffect(() => {
    const updateLastUpdatedText = () => {
      if (incident.length === 0) return;

      const mostRecentIncident = incident.reduce((latest, incident) => {
        const incidentDate = parseISO(incident.LastUpdated);
        return incidentDate > latest ? incidentDate : latest;
      }, new Date(0));

      const now = new Date();
      const diffInSeconds = differenceInSeconds(now, mostRecentIncident);
      let timeText = '';

      if (diffInSeconds < 60) {
        timeText = `${diffInSeconds} SECS AGO`;
      } else if (diffInSeconds < 3600) {
        const diffInMinutes = differenceInMinutes(now, mostRecentIncident);
        timeText = `${diffInMinutes} MINS AGO`;
      } else if (diffInSeconds < 86400) {
        const diffInHours = differenceInHours(now, mostRecentIncident);
        timeText = `${diffInHours} HOURS AGO`;
      } else {
        const diffInDays = differenceInDays(now, mostRecentIncident);
        timeText = `${diffInDays} DAYS AGO`;
      }

      setLastUpdatedText(timeText);
    };

    updateLastUpdatedText();
    const intervalId = setInterval(updateLastUpdatedText, 60000); // Update every minute

    return () => clearInterval(intervalId); // Cleanup interval on component unmount
  }, [incident]);

  return (
    <div className="bg-[#F8F8F8] pt-10 pb-[30px] px-6 flex flex-col h-full">
      <div className="mb-10">
        <p className="text-[12px] font-semibold leading-6 spacing tracking-[0.2em] mb-1">
          LAST UPDATED • <span className="text-[12px] font-semibold leading-6 spacing tracking-[0.2em]">{lastUpdatedText}</span>
        </p>
        <h2>Live Incidents</h2>
      </div>
      <div>
        {Object.keys(groupedIncidents).map(date => (
          <div key={date}>
            <p className="text-[12px] font-semibold leading-6 spacing tracking-[0.2em] mb-3 mt-3">
              {date}
            </p>
            <div className="flex flex-col gap-y-3">
              {groupedIncidents[date]
                .filter(incident => incident.IsActive === 1)
                .map(incident => {
                  LogErrors(user, logout, updateTokens, incident)
                  return (
                    <PersonCard
                      userData={incident}
                      type="emergency"
                      href={`/incidents/${incident.EmergencyId}`}
                      venueName={selectedVenue?.OrgPartnerFriendlyName}
                      key={incident?.EmergencyId}
                    />
                  );
                })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}